import { useQuery } from "@tanstack/react-query";
import { Link, Outlet, useRouteContext, useRouterState } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useTRPC } from "../api.ts";
import { Barras, Logo } from "./Barras.tsx";
import { IconoCampana, Sidebar } from "./Sidebar.tsx";

const CLAVE_PLEGADO = "gestiondoc.sidebar.plegado";

/**
 * Layout de la app. En escritorio el sidebar se pliega a una franja de 64 px (la preferencia
 * se recuerda en el navegador); en pantallas chicas se convierte en un panel que se abre desde
 * la barra superior y se cierra al navegar.
 */
export function Shell() {
  const { perfil: perfilInicial } = useRouteContext({ from: "/app" });
  const trpc = useTRPC();
  // El perfil del contexto de la ruta se carga una vez al entrar; esta consulta lo mantiene al
  // día, así el contador de pendientes cambia en cuanto alguien firma, devuelve o crea.
  const { data: perfil = perfilInicial } = useQuery({
    ...trpc.yo.perfil.queryOptions(),
    initialData: perfilInicial,
  });
  const [plegado, setPlegado] = useState(() => leerPlegado());
  const [abiertoMovil, setAbiertoMovil] = useState(false);
  const ruta = useRouterState({ select: (s) => s.location.pathname });

  // biome-ignore lint/correctness/useExhaustiveDependencies: se cierra el panel móvil al cambiar de ruta
  useEffect(() => setAbiertoMovil(false), [ruta]);

  useEffect(() => {
    if (!abiertoMovil) return;
    const cerrar = (e: KeyboardEvent) => e.key === "Escape" && setAbiertoMovil(false);
    window.addEventListener("keydown", cerrar);
    return () => window.removeEventListener("keydown", cerrar);
  }, [abiertoMovil]);

  function alternarPlegado() {
    setPlegado((p) => {
      guardarPlegado(!p);
      return !p;
    });
  }

  return (
    <div className="flex min-h-screen">
      {/* Barra superior, solo en pantallas chicas */}
      <header className="fixed inset-x-0 top-0 z-30 flex h-14 items-center gap-3 bg-marino-profundo px-4 text-white lg:hidden print:hidden">
        <button
          type="button"
          onClick={() => setAbiertoMovil(true)}
          aria-label="Abrir menú"
          className="grid size-9 place-items-center rounded-md hover:bg-white/10"
        >
          <IconoMenu />
        </button>
        <Logo variante="blanco" alto={26} />
        <span className="ml-auto text-rojo-marca">
          <Barras alto={10} />
        </span>
        <Link
          to="/avisos"
          aria-label={`Avisos (${perfil.notificacionesSinLeer} sin leer)`}
          className="relative grid size-9 place-items-center rounded-md hover:bg-white/10"
        >
          <IconoCampana />
          {perfil.notificacionesSinLeer > 0 && (
            <span className="absolute top-1.5 right-1.5 size-2 rounded-full bg-rojo-marca ring-2 ring-marino-profundo" />
          )}
        </Link>
      </header>

      {/* Fondo oscuro del panel móvil */}
      {abiertoMovil && (
        <button
          type="button"
          aria-label="Cerrar menú"
          onClick={() => setAbiertoMovil(false)}
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
        />
      )}

      <div
        className={`fixed inset-y-0 left-0 z-50 transition-transform duration-200 lg:static lg:translate-x-0 print:hidden ${
          abiertoMovil ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <Sidebar
          perfil={perfil}
          plegado={plegado}
          onAlternarPlegado={alternarPlegado}
          onCerrarMovil={() => setAbiertoMovil(false)}
        />
      </div>

      <main className="min-w-0 flex-1 px-4 pt-20 pb-6 lg:px-9 lg:py-8 print:p-0">
        <Outlet />
      </main>
    </div>
  );
}

function leerPlegado(): boolean {
  try {
    return localStorage.getItem(CLAVE_PLEGADO) === "1";
  } catch {
    return false;
  }
}

function guardarPlegado(v: boolean) {
  try {
    localStorage.setItem(CLAVE_PLEGADO, v ? "1" : "0");
  } catch {
    // sin almacenamiento: no pasa nada
  }
}

function IconoMenu() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path
        d="M3 5h14M3 10h14M3 15h14"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}
